<template>
  <div v-if="show" class="ai-generate-input-area">
    <div 
      class="ai-input-wrapper d-flex align-items-start gap-gd-1 p-gd-2"
      :class="{ 'is-invalid-border': $v.promptText.$error && $v.promptText.$dirty }"
    >
      <img
        src="@assets/images/icon/ai_gentext_active.svg"
        alt="AI Input"
        class="ai-input-icon"
      >

      <textarea
        ref="promptInput"
        id="ai-generate-input"
        v-model.trim="$v.promptText.$model"
        class="ai-input form-control fs-sa-14 fw-400 text-default p-0"
        :class="{
          'is-invalid': $v.promptText.$error && $v.promptText.$dirty,
          'pointer-events-none': inputDisabled
        }"
        :placeholder="$t('engine.aiGenerate.lblPlaceholder', languageEngine)"
        rows="1"
        @input="autoResize"
        @keydown.enter.exact.prevent="handleSend"
      ></textarea>

      <b-button
        :key="btnDisabled"
        class="ai-send-btn border-none p-0 pl-2 btn-makeweb-ai-gradient"
        :disabled="btnDisabled"
        @click="handleSend"
      >
        <i
          class="far fa-chevron-right fs-16 h-100"
          :class="btnDisabled ? 'text-black-hydrocarbon' : 'text-white'"
        />
      </b-button>
    </div>

    <!-- Validation Messages -->
    <div v-if="$v.promptText.$error" class="ai-input-errors mt-gd-half">
      <small v-if="!$v.promptText.required" class="text-danger fs-sa-12">
        {{ $t('engine.aiGenerate.validation.msgRequired', languageEngine) }}
      </small>
      <small v-else-if="!$v.promptText.maxLength" class="text-danger fs-sa-12">
        {{ $t('engine.aiGenerate.validation.msgMaxLength', languageEngine) }}
      </small>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, maxLength } from 'vuelidate/lib/validators';

export default {
  name: 'AIGenerateInput',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      promptText: '',
      isMultipleLines: false
    };
  },
  validations: {
    promptText: {
      required,
      maxLength: maxLength(500)
    }
  },
  computed: {
    ...mapGetters({
      languageEngine: 'designIframe/userPreference/languageEngine'
    }), 
    inputDisabled() {
      return this.isLoading
    },
    btnDisabled() {
      return !this.promptText.trim() || this.isLoading || this.$v.promptText.$invalid;
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.$nextTick(() => {
          this.$v.$reset();
          if (this.$refs.promptInput) {
            this.$refs.promptInput.focus();
          }
        });
      }
    }
  },
  methods: {  
    autoResize() {
      const el = this.$refs.promptInput
      if (!el) return
      el.style.height = el.scrollHeight + 'px'

      // จำกัดความสูงสูงสุด (เหมือน max-rows 6)
      const maxHeight = 160
      if (el.scrollHeight > maxHeight) {
        el.style.height = maxHeight + 'px'
        el.style.overflowY = 'auto'
      } else {
        el.style.overflowY = 'hidden'
      }
    },
    resetSize() {
      const el = this.$refs.promptInput
      if (!el) return
      el.style.height = 'auto'
    },
    handleSend() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const text = this.promptText.trim();
      if (!text || this.isLoading) return;

      this.$emit('send', text);
      // this.$v.promptText.$model = '';
      this.$v.$reset();
    },
    setPromptText(text) {
      this.promptText = text;
      this.$v.$reset();
      this.$nextTick(() => {
        if (this.$refs.promptInput) {
          this.$refs.promptInput.focus();
          this.autoResize()
        }
      });
    },
    resetValidation() {
      this.promptText = '';
      this.$v.$reset();
      this.resetSize();
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-input-wrapper {
  border-width: 1.5px;
  border-radius: 24px;
  transition: 0.2s;
  border: 1.5px solid transparent;
  background: linear-gradient(white, white) padding-box, linear-gradient(61deg, #28BEE3 0%, #7A5AF8 49.73%, #EE46BC 99.45%) border-box !important;
  &:focus-within {
    box-shadow: -2px 4px 16px 0 rgba(40, 190, 227, 0.15), 2px -4px 16px 0 rgba(238, 70, 188, 0.15) !important;
    -webkit-box-shadow: -2px 4px 16px 0 rgba(40, 190, 227, 0.15), 2px -4px 16px 0 rgba(238, 70, 188, 0.15) !important;
  }

  &.is-invalid-border {
    border-color: #dc3545 !important;
  }
}

.ai-input {
  outline: none;
  font-size: 14px;
  border: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
  min-height: 20px !important;
  &.is-invalid {
    border-color: transparent !important;
    box-shadow: none !important;
    background-image: none !important;
  }
  &.pointer-events-none {
    pointer-events: none !important;
  }
}

.ai-input-errors {
  padding-left: 12px;
}

.ai-send-btn {
  width: 24px;
  height: 21px;
  border-radius: 50%;
  background: linear-gradient(61deg, #28BEE3 0%, #7A5AF8 49.73%, #EE46BC 99.45%) !important;
  i {
    height: 100%;
    color: #FFFFFF !important;
  }

  &:disabled {
    background: #F4F7F9 !important;
    opacity: 1 !important;
    i {
      color: #515A63 !important;
    }
  }
  &:not(:disabled):hover {
    opacity: 0.85;
  }
}
</style>
